<template>
	<section v-if="teacherInfo" class="teacher-card">
		<img :src="teacherInfo.avatar" :alt="teacherInfo.nickname" class="teacher-card__avatar" />
		<div class="teacher-card__info">
			<h3 class="teacher-card__name">{{ teacherInfo.nickname }}</h3>
			<p class="teacher-card__meta">
				<img src="../../../images/start.png" alt="" class="teacher-card__star" />
				<span class="teacher-card__rating">{{ teacherInfo.rating }}</span>
				<span class="teacher-card__consult">
					{{ t("square.detail.articleTeacher.consultCount", { count: teacherInfo.serviceCount }) }}
				</span>
			</p>
		</div>
		<button type="button" class="teacher-card__contact" @click="handleContactTeacher">
			{{ t("square.detail.articleSidebar.contactTeacher") }}
		</button>
	</section>
</template>

<script setup>
import { computed, inject } from "vue";
import { getUserDetail } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { buildAuthorContactParams } from "../../../composables/useSquareDetailTracking";

const props = defineProps({
	squareInfo: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const { data: teacherDetail } = await useLazyAsyncData(
	() => `square-detail-m-teacher-${props.squareId}-${props.squareInfo?.uid || ""}`,
	async () => {
		const uid = props.squareInfo?.uid;
		if (!uid) return null;

		try {
			const res = await getUserDetail({ uid: String(uid) });
			return res?.data || null;
		} catch (error) {
			console.error("获取老师信息失败:", error);
			return null;
		}
	},
	{
		default: () => null,
		watch: [() => props.squareInfo?.uid, () => props.squareId],
	}
);

const teacherInfo = computed(() => {
	const data = teacherDetail.value;
	if (!data?.uid) return null;

	return {
		uid: data.uid,
		nickname: data.nickname || "",
		avatar: data.avatar || "",
		serviceCount: data.answers ?? "--",
		rating: data.score ?? "--",
	};
});

const handleContactTeacher = () => {
	if (!teacherInfo.value?.uid) return;
	if (logDetailEvent) {
		logDetailEvent(
			"article_author_contact_click",
			buildAuthorContactParams(props.squareInfo, teacherInfo.value.uid)
		);
	}
	navigateTo(`/seer/teacher-detail/${teacherInfo.value.uid}`);
};
</script>

<style lang="scss" scoped>
.teacher-card {
	display: flex;
	align-items: center;
	gap: 20px;
	margin: 20px 30px 32px;
	padding: 24px;
	background: #ffffff;
	border: 1px solid #efefef;
	border-radius: 16px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

	&__avatar {
		width: 96px;
		height: 96px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #f0f0f0;
	}

	&__info {
		flex: 1;
		min-width: 0;
	}

	&__name {
		margin: 0 0 8px;
		font-size: 32px;
		font-weight: 700;
		color: #333333;
		line-height: 1.3;
	}

	&__meta {
		width: fit-content;
		margin: 0;
		font-size: 26px;
		color: #595959;
		line-height: 1.4;
		background: #fdfae9;
		border-radius: 4px;
		padding: 5px;
	}

	&__star {
		width: 24px;
		height: 24px;
		object-fit: contain;
	}

	&__rating {
		margin-right: 8px;
	}

	&__contact {
		flex-shrink: 0;
		padding: 14px 24px;
		border: none;
		border-radius: 8px;
		background: #333333;
		color: #ffffff;
		font-size: 24px;
		font-weight: 500;
		cursor: pointer;
		white-space: nowrap;
	}
}
</style>
